"use client";

import { useSearchParams } from "next/navigation";
import { Suspense, useDeferredValue, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Loader2,
  PackageSearch,
  Search,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  fetchWelcomeCategories,
  type WelcomeCategoryDirectorySection,
  type WelcomeCategoryListItem,
} from "@/services/welcomeService";

const SECTION_PAGE_SIZE = 10;
const numberFormatter = new Intl.NumberFormat("en-IN");

const formatProductCount = (value: number) => {
  const normalized = Math.max(0, Number(value) || 0);
  return normalized === 0
    ? "No products yet"
    : `${numberFormatter.format(normalized)} products`;
};

const formatCompactCount = (value: number) =>
  Number(value || 0) > 0 ? numberFormatter.format(Number(value || 0)) : "0";

function DirectorySkeleton() {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={`directory-skeleton-${index}`} className="card-surface p-3.5 sm:p-4">
          <div className="h-4.5 w-40 rounded bg-muted animate-pulse" />
          <div className="mt-2.5 grid grid-cols-1 gap-2 md:[grid-template-columns:repeat(auto-fit,minmax(210px,1fr))]">
            {Array.from({ length: 4 }).map((__, innerIndex) => (
              <div
                key={`directory-skeleton-group-${index}-${innerIndex}`}
                className="rounded-md border border-border/60 bg-background/70 p-2.5"
              >
                <div className="h-3.5 w-24 rounded bg-muted animate-pulse" />
                <div className="mt-2.5 space-y-1">
                  {Array.from({ length: 6 }).map((___, itemIndex) => (
                    <div
                      key={`directory-skeleton-item-${index}-${innerIndex}-${itemIndex}`}
                      className="h-3 w-full rounded bg-muted animate-pulse"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function CategoryLinkRow({ item }: { item: WelcomeCategoryListItem }) {
  return (
    <div className="rounded-sm border border-transparent px-2 py-1 transition hover:border-border/60 hover:bg-muted/50">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-[12px] font-medium leading-4 text-foreground">
          {item.name}
        </p>
        {item.productCount > 0 ? (
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {formatCompactCount(item.productCount)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function DirectorySection({ section }: { section: WelcomeCategoryDirectorySection }) {
  return (
    <div className="card-surface overflow-hidden">
      <div className="border-b border-border/70 px-3.5 py-2.5 sm:px-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight text-foreground sm:text-lg">
              {section.name}
            </h2>
            {section.description ? (
              <p className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground sm:text-xs">
                {section.description}
              </p>
            ) : null}
          </div>
          <Badge variant="secondary" className="w-fit rounded-full px-2 py-0 text-[10px]">
            {formatCompactCount(section.productCount)} products
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-px bg-border/70 md:[grid-template-columns:repeat(auto-fit,minmax(210px,1fr))]">
        {section.groups.map((group) => (
          <div key={group.id} className="bg-card px-3 py-2.5">
            <div className="mb-1.5 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/85">
                  {group.name}
                </h3>
                {group.description ? (
                  <p className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground">
                    {group.description}
                  </p>
                ) : null}
              </div>
              {group.productCount > 0 ? (
                <span className="shrink-0 rounded-full bg-muted px-1.5 py-0 text-[9px] font-medium text-muted-foreground">
                  {formatCompactCount(group.productCount)}
                </span>
              ) : null}
            </div>

            <div className="space-y-0.5">
              {group.items.map((item) => (
                <CategoryLinkRow key={item.id} item={item} />
              ))}
            </div>

            {Number(group.moreCount || 0) > 0 ? (
              <p className="mt-1.5 px-2 text-[10px] text-muted-foreground">
                + {numberFormatter.format(Number(group.moreCount || 0))} more categories
              </p>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function WelcomeCategoriesPageContent() {
  const searchParams = useSearchParams();
  const queryFromUrl = searchParams.get("q")?.trim() ?? "";
  const [searchInput, setSearchInput] = useState(queryFromUrl);
  const deferredQuery = useDeferredValue(searchInput.trim());
  const latestQueryRef = useRef("");
  const [sections, setSections] = useState<WelcomeCategoryDirectorySection[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [mode, setMode] = useState<"browse" | "search">("browse");

  useEffect(() => {
    setSearchInput(queryFromUrl);
  }, [queryFromUrl]);

  useEffect(() => {
    let isActive = true;

    latestQueryRef.current = deferredQuery;
    setIsInitialLoading(true);
    setIsLoadingMore(false);
    setError(null);
    setSections([]);
    setNextCursor(null);
    setHasMore(false);

    const loadSections = async () => {
      try {
        const payload = await fetchWelcomeCategories({
          query: deferredQuery,
          limit: SECTION_PAGE_SIZE,
        });

        if (!isActive) return;

        setSections(payload.sections || []);
        setNextCursor(payload.nextCursor || null);
        setHasMore(Boolean(payload.hasMore));
        setMode(payload.mode || (deferredQuery ? "search" : "browse"));
      } catch (fetchError) {
        if (!isActive) return;
        setError("We couldn't load categories right now.");
      } finally {
        if (isActive) {
          setIsInitialLoading(false);
        }
      }
    };

    loadSections();

    return () => {
      isActive = false;
    };
  }, [deferredQuery, reloadTick]);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore || !nextCursor) return;

    const queryAtRequestTime = latestQueryRef.current;
    setIsLoadingMore(true);
    setError(null);

    try {
      const payload = await fetchWelcomeCategories({
        query: queryAtRequestTime,
        cursor: nextCursor,
        limit: SECTION_PAGE_SIZE,
      });

      if (latestQueryRef.current !== queryAtRequestTime) return;

      setSections((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const appended = (payload.sections || []).filter(
          (item) => !existingIds.has(item.id)
        );
        return [...prev, ...appended];
      });
      setNextCursor(payload.nextCursor || null);
      setHasMore(Boolean(payload.hasMore));
      setMode(payload.mode || (queryAtRequestTime ? "search" : "browse"));
    } catch (fetchError) {
      if (latestQueryRef.current !== queryAtRequestTime) return;
      setError("We couldn't load more categories right now.");
    } finally {
      if (latestQueryRef.current === queryAtRequestTime) {
        setIsLoadingMore(false);
      }
    }
  };

  const resetSearch = () => {
    setSearchInput("");
  };

  const loadedSectionCount = sections.length;

  return (
    <div className="w-full">
      <section className="border-b border-border/60 bg-background">
        <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5">
          <div className="card-surface p-3 sm:p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search categories"
                className="h-11 w-full rounded-lg border-border bg-muted/60 pl-11 text-sm shadow-sm focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:bg-background"
              />
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
              <span>
                {isInitialLoading
                  ? "Loading categories..."
                  : `${numberFormatter.format(loadedSectionCount)} sections loaded`}
              </span>
              {deferredQuery ? (
                <>
                  <span className="h-1 w-1 rounded-full bg-border" />
                  <span>Search: {deferredQuery}</span>
                </>
              ) : null}
              {deferredQuery ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={resetSearch}
                  className="h-6 rounded px-2 text-muted-foreground hover:text-foreground"
                >
                  Clear
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5">
          <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold tracking-tight text-foreground sm:text-lg">
              {mode === "search" ? "Matching categories" : "Browse all categories"}
            </h2>
            <p className="text-[11px] text-muted-foreground sm:text-xs">
              {mode === "search"
                ? "Explore relevant results grouped under their main business sections."
                : "Explore categories organised by major business sections."}
            </p>
          </div>

          {error ? (
            <div className="card-surface mb-4 border-destructive/20 bg-destructive/5 p-4">
              <div className="flex flex-col gap-4 text-sm sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-semibold text-foreground">
                    Categories are unavailable right now
                  </h2>
                  <p className="mt-1 text-muted-foreground">{error}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setReloadTick((current) => current + 1)}
                  className="text-foreground hover:text-foreground hover:bg-background"
                >
                  Try again
                </Button>
              </div>
            </div>
          ) : null}

          {isInitialLoading ? (
            <DirectorySkeleton />
          ) : sections.length > 0 ? (
            <div className="space-y-2.5">
              {sections.map((section) => (
                <DirectorySection key={section.id} section={section} />
              ))}
            </div>
          ) : (
            <div className="card-surface px-6 py-10 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <PackageSearch className="h-5 w-5" />
              </div>
              <h2 className="mt-3 text-lg font-semibold text-foreground">
                No categories found
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Try a broader search term or clear the current search to keep
                browsing.
              </p>
              {deferredQuery ? (
                <div className="mt-5">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetSearch}
                    className="text-foreground hover:text-foreground hover:bg-background"
                  >
                    Clear search
                  </Button>
                </div>
              ) : null}
            </div>
          )}

          <div className="mt-5 flex flex-col items-center gap-3">
            {hasMore ? (
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="h-10 gap-2 text-foreground hover:border-slate-300 hover:bg-slate-100 hover:text-foreground dark:hover:border-slate-700 dark:hover:bg-slate-800"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading more sections
                  </>
                ) : (
                  <>
                    Load more sections
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            ) : !isInitialLoading && sections.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                You&apos;ve reached the end of the available sections.
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function WelcomeCategoriesPage() {
  return (
    <Suspense
      fallback={
        <div className="w-full">
          <section className="border-b border-border/60 bg-background">
            <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5">
              <div className="card-surface p-3 sm:p-4">
                <div className="h-11 rounded-lg bg-muted/70 animate-pulse" />
                <div className="mt-2 h-4 w-40 rounded bg-muted animate-pulse" />
              </div>
            </div>
          </section>

          <section className="bg-slate-50 dark:bg-slate-900">
            <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5">
              <DirectorySkeleton />
            </div>
          </section>
        </div>
      }
    >
      <WelcomeCategoriesPageContent />
    </Suspense>
  );
}
